import * as React from "react";
import styled from "styled-components";
import { select } from "@hubper/theme";
import { animated, useSpring } from "react-spring";
import mergeStyles from "../../util/mergeStyles";
import { IconName } from "../../elements/Icon";
import NavigationItem, {
  WIDTH_COLLAPSED,
  WIDTH_EXPANDED
} from "./NavigationItem";

function findActiveIndex(routes: any[], currentPath: string) {
  const activeRoute = routes.find(route => {
    return route.path === "/"
      ? currentPath === "/"
      : currentPath.indexOf(route.path) > -1;
  });

  return routes.indexOf(activeRoute);
}

const Base = styled(animated.div)`
  position: relative;
  border-right: 1px solid ${select.color(["grey", 1])};
  overflow: hidden;
  height: 100%;
  background-color: white;
`;

const ActiveBar = styled(animated.div)`
  position: absolute;
  top: 12px;
  right: 0;
  width: 2px;
  height: 30px;
  background-color: ${select.color(["primary", 5])};
  z-index: 1;
  will-change: transform;
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  currentPath: string;
  routes: Array<{
    title: string;
    iconName: IconName;
    path: string;
    openItems?: number;
  }>;
  onClick: (path: string) => void;
  hide?: boolean;
};

function NavigationBar(
  { style, className, routes, onClick, currentPath, hide }: Props,
  ref: any
) {
  const [isOpen, setIsOpen] = React.useState(false);
  const timeout = React.useRef<number>(0);

  const activeIndex = findActiveIndex(routes, currentPath);
  const activeBarStyle = useSpring({
    transform: `translateY(${WIDTH_COLLAPSED * activeIndex}px)`,
    config: {
      tension: 300,
      clamp: true
    }
  });

  const collapsedWidth = hide ? 0 : WIDTH_COLLAPSED;

  const baseStyle = useSpring({
    width: isOpen ? WIDTH_EXPANDED : collapsedWidth,
    config: {
      tension: 300,
      clamp: true
    }
  });

  return (
    <Base
      ref={ref}
      style={mergeStyles(style, baseStyle)}
      className={className}
      onMouseOver={() => {
        clearTimeout(timeout.current);
        setIsOpen(true);
      }}
      onMouseOut={() => {
        timeout.current = setTimeout(() => {
          setIsOpen(false);
        }, 100);
      }}
    >
      <ActiveBar style={activeBarStyle} />
      {routes.map((route, index) => (
        <NavigationItem
          key={route.path}
          {...route}
          selected={activeIndex === index}
          onClick={onClick}
        />
      ))}
    </Base>
  );
}

export default React.forwardRef(NavigationBar);
